import imgEmptyNoData from 'assets/images/img_emptyview_no_data.png';
import AgencyHelper, { AgencyConstants } from 'features/Agency/agencyHelper';
import DealerHelper from 'features/ManageDealer/dealerHelper';
import ReconciliationReportHelper from 'features/ReconciliationReport/ReconciliationReportHelper';
import DropdownTableConfigColumn from 'general/components/DropdownTableConfigColumn';
import KTFormInput, {
    KTFormInputType,
} from 'general/components/OtherKeenComponents/Forms/KTFormInput';
import KTFormSearch from 'general/components/OtherKeenComponents/Forms/KTFormSearch';
import KTBSButton, { KTBSButtonColor, KTBSButtonSize } from 'general/components/OtherKeenComponents/KTBSButton';
import KTBSCard from 'general/components/OtherKeenComponents/KTBSCard';
import KTBSDropdown from 'general/components/OtherKeenComponents/KTBSDropdown';
import KTLabel, { KTLabelSize } from 'general/components/OtherKeenComponents/KTLabel';
import KTPagination, { KTPaginationColor } from 'general/components/OtherKeenComponents/KTPagination';
import KTTable from 'general/components/OtherKeenComponents/KTTable';
import tableStyle from 'general/components/OtherKeenComponents/KTTable/tableStyle';
import EmptyView from 'general/components/Others/EmptyView';
import MenuActionDropdown from 'general/components/Others/MenuActionDropdown';
import AppColors from 'general/constants/AppColors';
import UIHelper from 'general/helpers/UIHelper';
import Global from 'general/utils/Global';
import Utils from 'general/utils/Utils';
import useConfigTableColumn from 'hooks/useConfigTableColumn';
import useRouter from 'hooks/useRouter';
import _ from 'lodash';
import PropTypes from 'prop-types';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { AppCustomDialogHelper } from 'general/components/AppDialogCustom';
import ToastHelper from 'general/helpers/ToastHelper';
import AppConstants from 'general/constants/AppConstants';
import FileUploadView from 'general/components/quybka/Uploads/FileUploadView';
import DialogEditReconciliationView from 'features/ReconciliationReport/SaleReconciliationReport/components/DialogEditReconciliationView';
import JSZip from 'jszip';
import * as ExcelJS from 'exceljs';

LandlordReconciliationListScreen.propTypes = {
    agencyId: PropTypes.string,
};

const defaultColumnIds = [
    'RECONCILIATION_ID',
    'MONTH',
    'LANDLORD_ID',
    'LANDLORD_NAME',
    'ENERGY_AMOUNT',
    'PAID_ENERGY_AMOUNT',
    'TOTAL_ENERGY',
    'LANDLORD_REVENUE',
    'REVENUE',
    'STATUS',
    'DOCUMENT',
    'TYPE',
    'DATE',
];

function LandlordReconciliationListScreen(props) {
    // MARK: ---- PROPERTIES ----
    const { agencyId } = props;
    const { t } = useTranslation();
    const router = useRouter();
    const { user } = useSelector((state) => state.auth);
    const [reconciliationList, setReconciliationList] = useState([]);
    const [total, setTotal] = useState([]);
    const [filter, setFilter] = useState(Global.g_filterLandlordReconciliationList);
    const adminRole =
        user?.role === AppConstants.userRole.admin || user?.role === AppConstants.userRole.operator;
    const [isReady, setIsReady] = useState(false);

    useEffect(() => {
        handleGetLandlordReconciliationList();
    }, [filter, user])

    const fullColumns = useMemo(() => {
        return [
            {
                id: 'RECONCILIATION_ID',
                name: UIHelper.buildTableColumn('Mã đối soát'),
                selector: (row) => (
                    <span
                        className="cursor-pointer hover-opacity-60"
                        onClick={() => {
                            if (Utils.isValid(row?.document)) {
                                Utils.openInNewTab(Utils.getFullUrl(row?.document));
                            }
                        }}
                    >
                        {row?.reconciliationCode ?? row?.reconciliationId}
                    </span>
                ),
                width: '140px',
                style: tableStyle.columns.link,
            },
            {
                id: 'MONTH',
                name: UIHelper.buildTableColumn('Kỳ đối soát'),
                selector: (row) => Utils.formatDateTimeToString(row?.month, 'MM/yyyy'),
                width: '140px',
                style: tableStyle.columns.normal,
            },
            {
                id: 'LANDLORD_ID',
                omit: !adminRole,
                name: UIHelper.buildTableColumn('Mã chủ mặt bằng'),
                selector: (row) => row.agency?.agencyCode ?? DealerHelper.formatDealerCode(row?.agency?.agencyId),
                width: '130px',
                style: tableStyle.columns.normal,
            },
            {
                id: 'LANDLORD_NAME',
                omit: !adminRole,
                name: UIHelper.buildTableColumn('Tên chủ mặt bằng'),
                selector: (row) => (
                    <span
                        className="cursor-pointer hover-opacity-60"
                        onClick={() => {
                            AgencyHelper.Navigation.navigateToAgencyDetail(null, row?.agency);
                        }}
                    >
                        {row?.agency?.name}
                    </span>
                ),
                minWidth: '240px',
                style: tableStyle.columns.link,
            },
            {

                id: 'ENERGY_AMOUNT',
                name: UIHelper.buildTableColumn('Tổng HĐ tiền điện trạm sạc sau thuế', 'VNĐ'),
                selector: (row) => Utils.formatNumber(Math.round(row?.metadata?.totalEnergyCost)) ?? 0,
                minWidth: '240px',
                style: tableStyle.columns.normal,
                right: true,
            },
            {
                id: 'PAID_ENERGY_AMOUNT',
                name: UIHelper.buildTableColumn('Tiền điện CMB đã chi trả', 'VNĐ'),
                selector: (row) => {
                    const stations = (row?.metadata?.premises || []).filter(
                        item => item?.station?.isAgencyPayEnergyCost === 1
                    );

                    if (stations.length === 0) return 0;

                    return stations.reduce((totalStation, stationData) => {
                        const energyBills = stationData?.station?.energyBills || [];

                        const stationTotal = energyBills.reduce((sum, bill) => {
                            const amount = Number(bill?.amountBeforeTax ?? 0);
                            const taxRate = Number(bill?.taxRate ?? 0);
                            return sum + amount * (1 + taxRate);
                        }, 0);

                        return Utils.formatNumber(Math.round(totalStation + stationTotal));
                    }, 0);
                },
                minWidth: '200px',
                right: true,
                style: tableStyle.columns.normal,
            },
            {
                id: 'TOTAL_ENERGY',
                name: UIHelper.buildTableColumn('Tổng sản lượng điện sạc', 'kWh'),
                selector: (row) => Utils.formatNumber((_.sumBy(row?.metadata?.energyByStation, 'totalEnergy') / 1000)?.toFixed(3)),
                minWidth: '180px',
                style: tableStyle.columns.normal,
                right: true,
            },
            {
                id: 'LANDLORD_REVENUE',
                name: UIHelper.buildTableColumn('Doanh thu CMB được nhận', 'VNĐ'),
                selector: (row) => Utils.formatNumber(row?.totalRevenue - row?.taxAmount ?? 0),
                minWidth: '200px',
                right: true,
                style: tableStyle.columns.normal,
            },
            {
                id: 'STATUS',
                name: UIHelper.buildTableColumn('Trạng thái'),
                selector: (row) => (
                    <KTLabel
                        text={ReconciliationReportHelper.getReconciliationStatusName(row?.status, 'LANDLORD')}
                        isInline={true}
                        size={KTLabelSize.medium}
                        additionalClassName={`label-light-${ReconciliationReportHelper.getReconciliationStatusColor(
                            row?.status
                        )}`}
                    />
                ),
                minWidth: '140px',
                style: tableStyle.columns.normal,
            },
            {
                id: 'DOCUMENT',
                name: UIHelper.buildTableColumn('Tệp đính kèm'),
                selector: (row) => {
                    return row?.attachedFiles?.length > 0 ? (
                        <span
                            className="hover-opacity-60 cursor-pointer"
                            onClick={() => {
                                Utils.openInNewTab(row?.attachedFiles);
                            }}
                        >
                            {`Xem chi tiết`}
                        </span>
                    ) : (
                        <></>
                    );
                },
                width: '150px',
                style: tableStyle.columns.link,
            },
            {
                id: 'TYPE',
                name: UIHelper.buildTableColumn('Hình thức duyệt đối soát'),
                selector: (row) => ReconciliationReportHelper.getReconciliationTypeName(row?.reconciliationType ?? ''),
                width: '180px',
                style: tableStyle.columns.normal,
            },
            {
                id: 'DATE',
                name: UIHelper.buildTableColumn('Ngày tạo'),
                selector: (row) => (
                    <span>{Utils.formatDateTimeToString(row?.createdAt, 'DD/MM/YYYY')}</span>
                ),
                width: '120px',
                style: tableStyle.columns.normal,
            },
            {
                name: UIHelper.buildTableColumn('Thao tác'),
                width: '130px',
                style: {
                    position: 'sticky',
                    right: 0,
                    backgroundColor: '#fff',
                    borderLeft: '1px solid #ebedfe',
                },
                cell: (row) => {
                    return (
                        <MenuActionDropdown
                            menuAction={[
                                {
                                    iconElement: (
                                        <i
                                            className="fa-regular fa-eye"
                                            style={{ color: AppColors.gray[700], fontSize: '1.1rem' }}
                                        ></i>
                                    ),
                                    text: t('Xem chi tiết'),
                                    click: () => {
                                        router.navigate(`${row?.reconciliationId}`);
                                    },
                                    show: true,
                                },
                                {
                                    iconElement: (
                                        <i
                                            className="fa-regular fa-pen-to-square"
                                            style={{ color: AppColors.gray[700], fontSize: '1.1rem' }}
                                        ></i>
                                    ),
                                    text: t('edit'),
                                    click: () => {
                                        AppCustomDialogHelper.show(
                                            `Sửa đối soát tháng ${Utils.formatDateTimeToString(row?.month, 'MM/YYYY')}`,
                                            <DialogEditReconciliationView
                                                item={row}
                                                onCancel={() => {
                                                    AppCustomDialogHelper.hide();
                                                }}
                                                onDone={() => {
                                                    ToastHelper.showSuccess('Cập nhật đối soát thành công!')
                                                    AppCustomDialogHelper.hide();
                                                    handleGetLandlordReconciliationList();
                                                }}
                                            />,
                                            'md',
                                            true,
                                            true
                                        )
                                    },
                                    show: adminRole && (row?.reconciliationType === 'PAPER' && row?.status === 'COMPLETED'),
                                },
                                {
                                    iconElement: (
                                        <i
                                            className="far fa-file-signature"
                                            style={{ color: AppColors.gray[700], fontSize: '1.1rem' }}
                                        ></i>
                                    ),
                                    text: 'Duyệt & Ký đối soát',
                                    click: () => {
                                        ReconciliationReportHelper.handleAdminSignReconciliation(row, handleGetLandlordReconciliationList)
                                    },
                                    show: adminRole && row?.status === 'CREATED',
                                },
                                {
                                    iconElement: (
                                        <i
                                            className="far fa-file-signature"
                                            style={{ color: AppColors.gray[700], fontSize: '1.1rem' }}
                                        ></i>
                                    ),
                                    text: 'Ký đối soát',
                                    click: () => {
                                        ReconciliationReportHelper.handleAgencySignReconciliation(row, handleGetLandlordReconciliationList)
                                    },
                                    show: !adminRole && row?.status === 'ADMIN_SIGNED',
                                },
                                {
                                    iconElement: (
                                        <i
                                            className="far fa-file-chart-column"
                                            style={{ color: AppColors.gray[700], fontSize: '1.1rem' }}
                                        ></i>
                                    ),
                                    text: 'Xem file đối soát',
                                    click: () => {
                                        if (Utils.isValid(row?.document)) {
                                            Utils.openInNewTab(Utils.getFullUrl(row?.document));
                                        }
                                    },
                                    show: !!row?.document,
                                },
                                {
                                    iconElement: (
                                        <i
                                            className="far fa-receipt"
                                            style={{ color: AppColors.gray[700], fontSize: '1.1rem' }}
                                        ></i>
                                    ),
                                    text: 'Xem hóa đơn tiền điện trạm sạc',
                                    click: () => {
                                        if (row?.invoiceLookup) {
                                            window.open(row.invoiceLookup, '_blank');
                                        }
                                    },
                                    show: !!row?.invoiceLookup,
                                },
                                {
                                    iconElement: (
                                        <i
                                            className="far fa-download"
                                            style={{ color: AppColors.gray[700], fontSize: '1.1rem' }}
                                        ></i>
                                    ),
                                    text: 'Tải đối soát excel',
                                    click: () => Utils.openInCurrentTab(row?.documentXLSX),
                                    show: !!row?.documentXLSX,
                                },
                                {
                                    iconElement: (
                                        <i
                                            className="far fa-message-exclamation"
                                            style={{ color: AppColors.gray[700], fontSize: '1.1rem' }}
                                        ></i>
                                    ),
                                    text: 'Gửi phản hồi',
                                    click: () => ReconciliationReportHelper.showDialogReconciliationFeedback(row, adminRole, handleGetLandlordReconciliationList),
                                    show: !adminRole && row?.status !== 'AGENCY_SIGNED',
                                },
                                {
                                    iconElement: (
                                        <i
                                            className="far fa-message-exclamation"
                                            style={{ color: AppColors.gray[700], fontSize: '1.1rem' }}
                                        ></i>
                                    ),
                                    text: 'Xem phản hồi',
                                    click: () => ReconciliationReportHelper.showDialogReconciliationFeedback(row, adminRole, handleGetLandlordReconciliationList),
                                    show: adminRole && !!row?.feedback,
                                },
                                {
                                    iconElement: (
                                        <i
                                            className="far fa-file-check"
                                            style={{ color: AppColors.gray[700], fontSize: '1.1rem' }}
                                        ></i>
                                    ),
                                    text: 'Duyệt hóa đơn thuế',
                                    click: () => ReconciliationReportHelper.showDialogApprovalInvoice(row, adminRole, handleGetLandlordReconciliationList),
                                    show: adminRole && row?.agency?.type === 'ORGANIZATION' && row?.status === 'AGENCY_SIGNED' && !!row?.attachedFiles?.length,
                                },
                                {
                                    iconElement: (
                                        <i
                                            className="far fa-upload"
                                            style={{ color: AppColors.gray[700], fontSize: '1.1rem' }}
                                        ></i>
                                    ),
                                    text: 'Tải lên hóa đơn thuế',
                                    click: () => ReconciliationReportHelper.showDialogUploadInvoiceDocument(row, handleGetLandlordReconciliationList),
                                    show: row?.agency?.type === 'ORGANIZATION' && row?.status === 'AGENCY_SIGNED',
                                },
                            ]}
                        />
                    )
                },
            }
        ];
    }, [user, adminRole]);

    const prefKey = useMemo(() => {
        const userId = user.userId;
        const pathName = router.pathname;
        const key = `${userId}_${pathName}`;
        return key;
    }, [user, router]);
    const [fullColumnIds, setFullColumnIds] = useState(
        fullColumns?.map((row) => row?.id).filter(Boolean) || []
    );
    const {
        columnIds,
        columns,
        onChangeCheckboxValue,
        saveChanges,
        resetDefaultTableColumn,
        onSelectAll,
        onUnSelectAll,
        fullColumnsSorted,
        fullColumnsIdSorted,
    } = useConfigTableColumn(prefKey, fullColumnIds, defaultColumnIds, fullColumns);

    // MARK: ---- METHODS ----
    const handleGetLandlordReconciliationList = () => {
        let paramGetReconciliationList = { ...Global.g_filterLandlordReconciliationList, primaryRole: 'LANDLORD' };
        paramGetReconciliationList.agencyId = !adminRole ? user?.agency?.agencyId : '';
        if (adminRole || paramGetReconciliationList.agencyId) {
            AgencyHelper.API.requestGetReconciliationList(Utils.filterParams(paramGetReconciliationList))
                .then((data) => {
                    if (data) {
                        setReconciliationList(data?.reconciliations);
                        setTotal(data?.total);
                        setIsReady(true);
                    }
                })
        }
    };

    const handleViewFiles = (files) => {
        AppCustomDialogHelper.show(
            'Tệp đính kèm',
            <FileUploadView
                pathList={files}
                onCancel={() => {
                    AppCustomDialogHelper.hide();
                }}
            />,
            'md',
            false,
            true
        );
    };

    // xuat file excel
    const handleExportExcel = async (exportData, params) => {
        let exportDataList = exportData;
        if (_.isEmpty(exportDataList)) {
            ToastHelper.showError('Không có dữ liệu để tải xuống');
            return;
        }

        const zip = new JSZip();
        const sheetFileName = 'BBDS mặt bằng_Chủ mặt bằng_Tổng quan';
        const ext = '.xlsx';
        const folderName = 'BBDS mặt bằng_Chủ mặt bằng_Chi tiết';
        const zipFileName = `BBDS mặt bằng_Chủ mặt bằng_${Utils.formatDateTimeToString(params?.month, 'MMYYYY')}.zip`;

        const workbook = new ExcelJS.Workbook();
        const sheet = workbook.addWorksheet(sheetFileName, {
            headerFooter: { firstHeader: sheetFileName },
        });

        sheet.mergeCells('A1:K1');
        sheet.getCell('A1').value = `BÁO CÁO ĐỐI SOÁT MẶT BẰNG - CHỦ MẶT BẰNG`;
        sheet.getCell('A1').font = {
            name: 'Times New Roman',
            family: 4,
            size: 11,
            bold: true,
            color: { argb: 'FFFFFFFF' },
        };
        sheet.getCell('A1').alignment = { vertical: 'middle', horizontal: 'center' };
        sheet.getCell('A1').fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: '29618E' },
            color: { argb: 'FFFFFF' },
        };
        sheet.getCell('A1').border = {
            top: { style: 'thin' },
            left: { style: 'thin' },
            bottom: { style: 'thin' },
            right: { style: 'thin' },
        };
        sheet.getRow(1).height = 25;

        const columns = [
            { key: 'stt', width: 7 },
            { key: 'reconciliationCode', width: 18 },
            { key: 'month', width: 18 },
            { key: 'agencyCode', width: 18 },
            { key: 'agencyName', width: 35 },
            { key: 'totalEnergyCost', width: 40 },
            { key: 'totalEVNEnergyCost', width: 35 },
            { key: 'totalEnergy', width: 35 },
            { key: 'totalRevenue', width: 40 },
            { key: 'status', width: 20 },
            { key: 'date', width: 22 },
        ];
        sheet.columns = columns;

        sheet.getRow(2).height = 30;
        sheet.getRow(2).font = {
            name: 'Times New Roman',
            family: 4,
            size: 11,
            bold: true,
        };
        sheet.getRow(2).alignment = {
            vertical: 'middle',
            horizontal: 'center',
            wrapText: true,
        };

        sheet.getCell('A2').value = 'STT';
        sheet.getCell('B2').value = 'Mã đối soát';
        sheet.getCell('C2').value = 'Kỳ đối soát';
        sheet.getCell('D2').value = 'Mã chủ mặt bằng';
        sheet.getCell('E2').value = 'Tên chủ mặt bằng';
        sheet.getCell('F2').value = 'Tổng HĐ tiền điện trạm sạc sau thuế (VNĐ)';
        sheet.getCell('G2').value = 'Tiền điện CMB đã chi trả (VNĐ)';
        sheet.getCell('H2').value = 'Tổng sản lượng điện sạc (kWh)';
        sheet.getCell('I2').value = 'Doanh thu chủ mặt bằng được nhận (VNĐ)';
        sheet.getCell('J2').value = 'Trạng thái';
        sheet.getCell('K2').value = 'Ngày tạo';

        ['A2:K2'].forEach((range) => {
            sheet.getRow(range.split(':')[0].slice(1)).eachCell({ includeEmpty: true }, (cell) => {
                cell.border = {
                    top: { style: 'thin' },
                    left: { style: 'thin' },
                    bottom: { style: 'thin' },
                    right: { style: 'thin' },
                };
            });
        });

        let i = 1;
        exportDataList?.forEach((row) => {
            const dataRow = sheet.addRow({
                stt: i++,
                reconciliationCode: row?.reconciliationCode ?? row?.reconciliationId,
                month: Utils.formatDateTimeToString(row?.month, 'MM/yyyy'),
                agencyCode: row.agency?.agencyCode ?? row?.agency?.agencyId,
                agencyName: row?.agency?.name,
                totalEnergyCost: Utils.formatNumber(Math.round(row?.metadata?.totalEnergyCost)) ?? 0,
                totalEVNEnergyCost: '',
                totalEnergy: Utils.formatNumber((_.sumBy(row?.metadata?.energyByStation, 'totalEnergy') / 1000)?.toFixed(3)),
                totalRevenue: Utils.formatNumber(Math.round(row?.amount - row?.amount * row?.taxRate)),
                status: ReconciliationReportHelper.getReconciliationStatusName(row?.status, 'LANDLORD'),
                date: row?.createdAt ? Utils.formatDateTimeToString(row?.createdAt, 'DD/MM/YYYY - HH:mm') : '',
            });

            dataRow.eachCell({ includeEmpty: true }, (cell, colNumber) => {
                cell.border = {
                    top: { style: 'thin' },
                    left: { style: 'thin' },
                    bottom: { style: 'thin' },
                    right: { style: 'thin' },
                };
                if (colNumber === 1) {
                    cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
                } else if (colNumber >= 6 && colNumber <= 9) {
                    cell.alignment = { vertical: 'middle', horizontal: 'right', wrapText: true };
                } else {
                    cell.alignment = { vertical: 'middle', horizontal: 'left', wrapText: true };
                }
            });
        });

        sheet.eachRow((row, rowNumber) => {
            if (rowNumber >= 3) {
                row.font = {
                    name: 'Times New Roman',
                    family: 4,
                    size: 11,
                    bold: false,
                };
            }
        });

        const mainFileBuffer = await workbook.xlsx.writeBuffer();
        zip.file(`${sheetFileName}${ext}`, mainFileBuffer);

        const detailFolder = zip.folder(folderName);

        const fetchPromises = exportDataList?.map(async (row, index) => {
            if (row?.documentXLSX) {
                try {
                    const fileName = await Utils.getFileNameFromPath(row?.documentXLSX);
                    const file = await Utils.urlToFile(row?.documentXLSX, fileName);
                    const fileBuffer = await file.arrayBuffer();
                    const zipFileName = `${row?.reconciliationCode}_${Utils.formatDateTimeToString(params?.month, 'MMYYYY')}_BBDS mặt bằng_Chủ mặt bằng.company${ext}`;
                    detailFolder.file(zipFileName, fileBuffer);
                } catch (error) {
                    console.error(`Lỗi khi xử lý file ${row.documentXLSX}:`, error.message);
                    ToastHelper.showError(`Không tải được file ${row?.reconciliationCode ?? `item_${index + 1}`}: ${error.message}`);
                }
            }
        });

        await Promise.all(fetchPromises);

        try {
            const content = await zip.generateAsync({ type: 'blob' });
            const url = window.URL.createObjectURL(content);
            const anchor = document.createElement('a');
            anchor.href = url;
            anchor.download = zipFileName;
            anchor.click();
            window.URL.revokeObjectURL(url);
            ToastHelper.showSuccess('Tải xuống file ZIP thành công');
        } catch (error) {
            console.error('Lỗi khi tạo file ZIP:', error.message);
            ToastHelper.showError('Không thể tạo file ZIP');
        } finally {
            AppCustomDialogHelper.hide();
        }
    };

    return (
        <div className='m-4'>
            {isReady ? (
                <KTBSCard
                    borderRadius="10"
                    title={t('Báo cáo đối soát chủ mặt bằng')}
                    description={
                        <div>
                            <span
                                className="font-weight-bolder"
                                style={{ fontSize: '14px', color: AppColors.gray[800] }}
                            >
                                {t('total')}:{' '}
                            </span>
                            <span
                                className="font-weight-bolder"
                                style={{ fontSize: '14px', color: AppColors.primary }}
                            >
                                {Utils.formatNumber(total ?? reconciliationList?.length)} đối soát
                            </span>
                        </div>
                    }
                    additionalHeaderClassName="pt-4 pb-2"
                    additionalBodyClassName=""
                    additionalFooterClassName=""
                    hideDividerHeader
                    hideDividerFooter
                    toolbar={
                        <div className="d-flex gap-3">
                            {adminRole &&
                                <KTBSButton
                                    color={KTBSButtonColor.primary}
                                    title={t('Tải xuống')}
                                    size={KTBSButtonSize.default}
                                    borderRadius={5}
                                    buttonTitleFontWeight="600"
                                    iconElement={<i className="fa-regular fa-download" style={{ color: '#FFFFFF', fontSize: 15 }} />}
                                    onPress={async () => {
                                        try {
                                            const params = { ..._.omit(Global.g_filterLandlordReconciliationList, ['limit', 'page']), primaryRole: 'LANDLORD', limit: 1000000 }
                                            const { reconciliations = [] } = await AgencyHelper.API.requestGetReconciliationList(Utils.filterParams(params));
                                            return handleExportExcel(reconciliations, params);
                                        } catch (error) {
                                            ToastHelper.showError(`Lỗi khi lấy dữ liệu: ${error.message}`);
                                            return [];
                                        }
                                    }}
                                    height={43}
                                    buttonTitleFontSize={14}
                                    buttonTitleColor='#FFFFFF'
                                    additionalClassName='font-weight-bold d-flex align-items-center'
                                />
                            }
                        </div>
                    }
                    subToolbar={
                        <div className="w-100 d-flex align-items-end flex-wrap">
                            {/* tim kiem */}
                            <div className="pl-0 pr-0 pr-md-4 col-xl-3 col-md-4 py-2">
                                {UIHelper.buildFormLabel('Tìm kiếm')}
                                <KTFormSearch
                                    name="search"
                                    placeholder={adminRole ? "Theo mã đối soát, mã chủ mặt bằng, tên chủ mặt bằng" : "Theo mã đối soát..."}
                                    value={filter.q}
                                    onSubmit={(text) => {
                                        Global.g_filterLandlordReconciliationList = {
                                            ...Global.g_filterLandlordReconciliationList,
                                            q: text,
                                            page: 0,
                                        };
                                        setFilter(Global.g_filterLandlordReconciliationList);
                                    }}
                                />
                            </div>

                            {/* loc theo thang doi soat */}
                            <div className="pl-0 pr-0 pr-md-4 col-xl-3 col-md-4 py-2">
                                {UIHelper.buildFormLabel('Kỳ đối soát')}
                                <KTFormInput
                                    name="month"
                                    type={KTFormInputType.month}
                                    onChange={(value) => {
                                        Global.g_filterLandlordReconciliationList = {
                                            ...Global.g_filterLandlordReconciliationList,
                                            month: value,
                                            page: 0,
                                        };
                                        setFilter(Global.g_filterLandlordReconciliationList);
                                    }}
                                    value={filter.month}
                                    placeholder='MM/YYYY'
                                />
                            </div>

                            {/* loc theo trang thai */}
                            <div className="pl-0 pr-0 pr-md-4 col-xl-3 col-md-4 py-2">
                                {UIHelper.buildFormLabel('Trạng thái')}
                                <KTBSDropdown
                                    name="status"
                                    toggleElement={
                                        <button
                                            className="btn btn-white border-secondary dropdown-toggle w-100 d-flex align-items-center justify-content-between"
                                            type="button"
                                        >
                                            {ReconciliationReportHelper.filterStatusOptions('LANDLORD').find(item => item.value === filter.status)?.name}
                                        </button>
                                    }
                                    selectedValue={filter.status}
                                    dropdownMenuItems={ReconciliationReportHelper.filterStatusOptions('LANDLORD')}
                                    dropdownMenuClassName="overflow-auto w-100"
                                    dropdownMenuStyle={{ maxHeight: '30vh' }}
                                    onChange={(value) => {
                                        Global.g_filterLandlordReconciliationList = {
                                            ...Global.g_filterLandlordReconciliationList,
                                            status: value,
                                            page: 0,
                                        };
                                        setFilter(Global.g_filterLandlordReconciliationList);
                                    }}
                                    isDropdownForm={true}
                                />
                            </div>

                            {/* cau hinh cot */}
                            <div className="pr-4 py-2">
                                <DropdownTableConfigColumn
                                    columnIds={columnIds}
                                    fullColumns={fullColumns}
                                    fullColumnIds={fullColumnsIdSorted}
                                    onChangeCheckboxValue={onChangeCheckboxValue}
                                    saveChanges={saveChanges}
                                    resetDefaultTableColumn={() => {
                                        setFullColumnIds(defaultColumnIds);
                                        resetDefaultTableColumn();
                                    }}
                                    onChangeFullColumnIds={(ids) => {
                                        setFullColumnIds(ids);
                                        const key = prefKey + '_FULL_COLUMN_IDS';
                                        localStorage.setItem(key, ids.join('|'));
                                    }}
                                    onSelectAll={onSelectAll}
                                    onUnSelectAll={onUnSelectAll}
                                />
                            </div>

                            <div className="pr-0 py-2">
                                {/* {adminRole &&
                                <KTBSButton
                                    color={KTBSButtonColor.secondary}
                                    title={t('Tải xuống')}
                                    size={KTBSButtonSize.default}
                                    borderRadius={5}
                                    buttonTitleFontWeight="600"
                                    iconElement={<i className="fa-regular fa-download" style={{ color: AppColors.gray[800], fontSize: 15 }} />}
                                    onPress={() => console.log('Tải xuống')}
                                    height={43}
                                    buttonTitleFontSize={14}
                                    buttonTitleColor={AppColors.gray[800]}
                                    additionalClassName='btn btn-light font-weight-bold d-flex align-items-center'
                                />
                            } */}
                            </div>
                        </div>
                    }
                    footer={
                        <KTPagination
                            currentPage={filter.page + 1}
                            rowCount={total ?? reconciliationList?.length}
                            rowsPerPage={filter.limit}
                            currentItemInPageCount={reconciliationList.length}
                            onChangePage={(newPage) => {
                                const iNewPage = parseInt(newPage);
                                Global.g_filterLandlordReconciliationList = {
                                    ...Global.g_filterLandlordReconciliationList,
                                    page: iNewPage - 1,
                                };
                                setFilter(Global.g_filterLandlordReconciliationList);
                            }}
                            onChangeRowsPerPage={(newRowsPerPage) => {
                                const iNewRowsPerPage = parseInt(newRowsPerPage);
                                Global.g_filterLandlordReconciliationList = {
                                    ...Global.g_filterLandlordReconciliationList,
                                    limit: iNewRowsPerPage,
                                    page: 0,
                                };
                                setFilter(Global.g_filterLandlordReconciliationList);
                            }}
                            color={KTPaginationColor.success}
                            appendText="đối soát"
                        />
                    }
                    body={
                        <KTTable
                            columns={columns}
                            items={reconciliationList}
                            selectableRows={false}
                            striped={false}
                            compactGrid={false}
                            onClickedRow={(row) => { }}
                            onSelectedRowsChanged={(rows) => { }}
                            onDoubleClickedRow={(rows) => { }}
                            noDataComponent={
                                <EmptyView
                                    icon={imgEmptyNoData}
                                    title="Không có dữ liệu để hiển thị"
                                    text={<span>Vui lòng kiểm tra kết nối mạng hoặc tải lại trang.</span>}
                                />
                            }
                        />
                    }
                />
            ) : <></>}
        </div>
    );
}

export default LandlordReconciliationListScreen;
